import Component from '@glimmer/component';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq } from 'ember-truth-helpers';

type PillColor = 'carbon' | 'pistachio' | 'cilantro' | 'papaya' | 'gem';

/**
 * Enumerate valid tailwind color class names so that PurgeCSS does not remove them
 */
const SAFE_CLASSES = [
  // carbon
  'bg-carbon-10',
  'text-carbon-60',
  'fill-carbon-30',

  // pistachio
  'bg-pistachio-10',
  'text-pistachio-60',
  'fill-pistachio-30',

  // cilantro
  'bg-cilantro-10',
  'text-cilantro-60',
  'fill-cilantro-30',

  // papaya
  'bg-papaya-10',
  'text-papaya-60',
  'fill-papaya-30',

  // gem
  'bg-gem-10',
  'text-gem-60',
  'fill-gem-30',
];

function validClassName(className: string): string {
  if (!SAFE_CLASSES.includes(className)) {
    throw new Error(
      `<Ui::Classic::Pillbox> Attempted to use ${className} which does not appear in the SAFE_CLASSES whitelist`,
    );
  }
  return className;
}

interface PillboxSignature {
  Args: {
    color?: PillColor;
    size?: 'sm' | 'md';
    icon?: string;
  };
  Element: HTMLSpanElement;
  Blocks: {
    default: [];
  };
}

export default class Pillbox extends Component<PillboxSignature> {
  get color(): PillColor {
    return this.args.color ?? 'carbon';
  }

  get fillClassName(): string {
    return validClassName(`fill-${this.color}-30`);
  }

  get bgClassName(): string {
    return validClassName(`bg-${this.color}-10`);
  }

  get textClassName(): string {
    return validClassName(`text-${this.color}-60`);
  }

  <template>
    <span
      class="ui__pillbox inline-flex items-center space-x-1 leading-none whitespace-nowrap rounded-full
        {{this.bgClassName}}
        {{this.textClassName}}
        {{if (eq @size 'sm') 'px-[0.425rem] py-1' 'px-2 py-1'}}"
      ...attributes
    >
      {{#if @icon}}
        {{svgJar @icon class=this.fillClassName data-test-icon=@icon}}
      {{/if}}
      <span class="{{if (eq @size 'sm') 'text-[0.5rem] font-bold'}}">{{yield}}</span>
    </span>
  </template>
}
